import { useQuery } from "@tanstack/react-query";
import { getPlayerStatistics, LEAGUE_IDS, type PlayerStatistics } from "@/lib/apiSports";
import { useAuth } from "@/contexts/AuthContext";
import type { FavoritePlayer } from "@/hooks/useUserFavorites";

// Career season data structure
export interface CareerSeason {
  season: string;
  team: string;
  teamLogo?: string;
  league: string;
  gp: number;
  goals: number;
  assists: number;
  points: number;
  plusMinus: number;
  pim: number;
  // Goalie specific
  wins?: number;
  losses?: number;
  gaa?: number;
  svPct?: number;
  shutouts?: number;
  isGoalie: boolean;
}

// Career totals
export interface CareerTotals {
  gp: number;
  goals: number;
  assists: number;
  points: number;
  pim: number;
  // Goalie
  wins?: number;
  losses?: number;
  shutouts?: number;
}

export interface PlayerCareerData {
  seasons: CareerSeason[];
  totals: CareerTotals;
  isLoading: boolean;
  error: string | null;
}

// Map league name to API league ID
const getLeagueId = (league: string): number | null => {
  const leagueMap: Record<string, number> = {
    NHL: LEAGUE_IDS.NHL,
    AHL: LEAGUE_IDS.AHL,
    ECHL: LEAGUE_IDS.ECHL,
    KHL: LEAGUE_IDS.KHL,
    SHL: LEAGUE_IDS.SHL,
    Allsvenskan: LEAGUE_IDS.ALLSVENSKAN,
    Liiga: LEAGUE_IDS.LIIGA,
    Extraliga: LEAGUE_IDS.EXTRALIGA,
    DEL: LEAGUE_IDS.DEL,
    NL: LEAGUE_IDS.NL,
  };
  return leagueMap[league] || null;
};

// Get season range to query (last 8 seasons)
const getSeasonRange = (): number[] => {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const currentSeason = month >= 7 ? year : year - 1;
  
  const seasons: number[] = [];
  for (let i = 0; i < 8; i++) {
    seasons.push(currentSeason - i);
  }
  return seasons;
};

// Transform API response to career season format
const transformSeasonStats = (
  apiData: PlayerStatistics,
  season: number
): CareerSeason | null => {
  const stats = apiData.statistics?.[0] as Record<string, unknown> | undefined;
  if (!stats) return null;

  const games = stats.games as { appearences?: number; position?: string; minutes?: number } | undefined;
  const goalsData = stats.goals as { total?: number; assists?: number; against?: number; saves?: number } | undefined;
  const team = stats.team as { name?: string; logo?: string } | undefined;
  const league = stats.league as { name?: string; season?: number } | undefined;
  const penalty = stats.penalty as { minutes?: number } | undefined;

  const gp = games?.appearences || 0;
  if (gp === 0) return null; // Skip seasons with no games

  const isGoalie = games?.position === "G";
  const goals = goalsData?.total || 0;
  const assists = goalsData?.assists || 0;

  const seasonStr = `${season}-${String(season + 1).slice(2)}`;

  if (isGoalie) {
    const goalsAgainst = goalsData?.against || 0;
    const saves = goalsData?.saves || 0;
    const minutes = games?.minutes || 0;
    const gaa = minutes > 0 ? (goalsAgainst / minutes) * 60 : 0;
    const shotsAgainst = goalsAgainst + saves;
    const svPct = shotsAgainst > 0 ? saves / shotsAgainst : 0;

    return {
      season: seasonStr,
      team: team?.name || "Unknown",
      teamLogo: team?.logo,
      league: league?.name || "",
      gp,
      goals: 0,
      assists: 0,
      points: 0,
      plusMinus: 0,
      pim: penalty?.minutes || 0,
      wins: (stats as Record<string, unknown>).wins as number || 0,
      losses: (stats as Record<string, unknown>).losses as number || 0,
      gaa,
      svPct,
      shutouts: (stats as Record<string, unknown>).shutouts as number || 0,
      isGoalie: true,
    };
  }

  return {
    season: seasonStr,
    team: team?.name || "Unknown",
    teamLogo: team?.logo,
    league: league?.name || "",
    gp,
    goals,
    assists,
    points: goals + assists,
    plusMinus: (stats.plusminus as number) || 0,
    pim: penalty?.minutes || 0,
    isGoalie: false,
  };
};

// Fetch career stats for a player across multiple seasons
async function fetchPlayerCareerStats(
  playerId: number,
  league: string
): Promise<CareerSeason[]> {
  const leagueId = getLeagueId(league);
  if (!leagueId) return [];

  const seasons = getSeasonRange();
  const results: CareerSeason[] = [];

  // Fetch stats for each season in parallel
  const fetchPromises = seasons.map(async (season) => {
    try {
      const response = await getPlayerStatistics({
        league: leagueId,
        season,
        player: playerId,
      });

      if (response.response && response.response.length > 0) {
        const seasonData = transformSeasonStats(response.response[0], season);
        if (seasonData) {
          return seasonData;
        }
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch stats for season ${season}:`, error);
      return null;
    }
  });

  const settledResults = await Promise.allSettled(fetchPromises);
  
  settledResults.forEach((result) => {
    if (result.status === "fulfilled" && result.value) {
      results.push(result.value);
    }
  });

  // Sort by season (oldest first)
  results.sort((a, b) => a.season.localeCompare(b.season));

  return results;
}

// Calculate career totals from seasons
function calculateCareerTotals(seasons: CareerSeason[]): CareerTotals {
  const isGoalie = seasons.some((s) => s.isGoalie);

  if (isGoalie) {
    return seasons.reduce(
      (acc, season) => ({
        gp: acc.gp + season.gp,
        goals: 0,
        assists: 0,
        points: 0,
        pim: acc.pim + season.pim,
        wins: (acc.wins || 0) + (season.wins || 0),
        losses: (acc.losses || 0) + (season.losses || 0),
        shutouts: (acc.shutouts || 0) + (season.shutouts || 0),
      }),
      { gp: 0, goals: 0, assists: 0, points: 0, pim: 0, wins: 0, losses: 0, shutouts: 0 }
    );
  }

  return seasons.reduce(
    (acc, season) => ({
      gp: acc.gp + season.gp,
      goals: acc.goals + season.goals,
      assists: acc.assists + season.assists,
      points: acc.points + season.points,
      pim: acc.pim + season.pim,
    }),
    { gp: 0, goals: 0, assists: 0, points: 0, pim: 0 }
  );
}

export function usePlayerCareerStats(player: FavoritePlayer | null) {
  const { user, subscription } = useAuth();
  
  // Only fetch for premium users
  const hasPremiumAccess = subscription.subscribed && 
    (subscription.tier === "pro" || subscription.tier === "elite");

  const query = useQuery({
    queryKey: ["playerCareerStats", player?.id, player?.league],
    queryFn: async () => {
      if (!player) return { seasons: [], totals: { gp: 0, goals: 0, assists: 0, points: 0, pim: 0 } };
      
      const seasons = await fetchPlayerCareerStats(player.id, player.league);
      const totals = calculateCareerTotals(seasons);
      
      return { seasons, totals };
    },
    enabled: !!player && !!user && hasPremiumAccess,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour cache
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    seasons: query.data?.seasons || [],
    totals: query.data?.totals || { gp: 0, goals: 0, assists: 0, points: 0, pim: 0 },
    isLoading: query.isLoading,
    error: query.error?.message || null,
  };
}

export default usePlayerCareerStats;
